import { useState } from "react";
import { CalendarDays } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMutation } from "@apollo/client/react";
import { Button, Calendar, Eyebrow, Popover, PopoverContent, PopoverTrigger, Text } from "@athena/ui";
import { UpdateClientSoberDateDocument } from "@/graphql/clients.generated";
import { formatNumericDay, toCalendarDate, formatDay, formatCalendarDate } from "../helpers/client";

interface ResetSoberDateProps {
    clientId: string;
    dischargeDate: string | undefined;
    today: Date;
    onCancel: () => void;
    onSaved: () => void;
}

export function ResetSoberDate({ clientId, dischargeDate, today, onCancel, onSaved }: ResetSoberDateProps) {
    const { t } = useTranslation();
    const [
        soberDate,
        setSoberDate,
    ] = useState<Date>(today);
    const [
        calendarOpen,
        setCalendarOpen,
    ] = useState<boolean>(false);

    const [
        updateSoberDate,
        { loading: saving },
    ] = useMutation(UpdateClientSoberDateDocument);

    const dateText = formatNumericDay(soberDate);

    const save = async () => {
        try {
            await updateSoberDate({ variables: { clientId, soberDate: toCalendarDate(soberDate) } });
        } catch {
            toast.error(t("admin.alumni.profile.resetSoberDate.saveFailed"));
            return;
        }
        toast.success(t("admin.alumni.profile.resetSoberDate.saved", { date: formatDay(soberDate) }));
        onSaved();
    };

    return (
        <div className="border-destructive bg-destructive/5 rounded-xl border p-4">
            <Eyebrow>{t("admin.alumni.profile.resetSoberDate.title")}</Eyebrow>
            {dischargeDate ? (
                <Text className="text-xs" muted>
                    {t("admin.alumni.profile.resetSoberDate.description", {
                        date: formatCalendarDate(dischargeDate),
                    })}
                </Text>
            ) : null}
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        aria-label={`${t("admin.alumni.profile.resetSoberDate.dateLabel")}: ${dateText}`}
                        className="mt-3 w-full justify-between font-mono tracking-normal normal-case"
                        rightIcon={<CalendarDays className="h-4 w-4" />}
                    >
                        <span className="text-text-heading">{dateText}</span>
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-fit p-3" align="start">
                    <Calendar
                        mode="single"
                        required
                        selected={soberDate}
                        defaultMonth={soberDate}
                        onSelect={(date) => {
                            setSoberDate(date);
                            setCalendarOpen(false);
                        }}
                        // A sober date is a day already lived; it cannot start in the future.
                        disabled={{ after: today }}
                    />
                </PopoverContent>
            </Popover>
            <div className="mt-3 flex flex-row gap-2">
                <Button variant="outline" onClick={onCancel}>
                    {t("common.actions.cancel")}
                </Button>
                <Button onClick={save} disabled={saving}>
                    {t("common.actions.save")}
                </Button>
            </div>
        </div>
    );
}
